import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiArrowRight, FiBook, FiUser, FiCalendar, FiMail, FiPhone, FiFileText } from 'react-icons/fi';
import Modal from 'react-modal';
import LogoTipo from '../../../assets/logo.png';

Modal.setAppElement('#root');

const PublicApplicationForm = () => {
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    dataNascimento: '',
    genero: '',
    docIdentificacao: '',
    curso: '',
    turno: '',
    email: '',
    telefone: ''
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Obter candidaturas existentes do localStorage
    const existingApplications = JSON.parse(localStorage.getItem('applications')) || [];
    
    // Criar nova candidatura
    const newApplication = {
      ...formData,
      id: existingApplications.length + 1,
      dataInscricao: new Date().toISOString()
    };
    
    // Adicionar ao array e salvar no localStorage
    const updatedApplications = [...existingApplications, newApplication];
    localStorage.setItem('applications', JSON.stringify(updatedApplications));
    
    // Limpar formulário e mostrar modal
    setFormData({
      nomeCompleto: '',
      dataNascimento: '',
      genero: '',
      docIdentificacao: '',
      curso: '',
      turno: '',
      email: '',
      telefone: ''
    });
    
    setModalIsOpen(true);
    setCurrentStep(1);
  };

  const steps = [
    {
      title: "Informações Pessoais",
      icon: <FiUser />,
      fields: [
        { name: 'nomeCompleto', label: 'Nome Completo', type: 'text', icon: <FiUser />, required: true },
        { name: 'dataNascimento', label: 'Data de Nascimento', type: 'date', icon: <FiCalendar />, required: true },
        { name: 'genero', label: 'Gênero', type: 'select', options: ['Masculino', 'Feminino', 'Outro'], icon: <FiUser />, required: true },
        { name: 'docIdentificacao', label: 'Documento de Identificação', type: 'text', icon: <FiFileText />, required: true }
      ]
    },
    {
      title: "Informações Acadêmicas",
      icon: <FiBook />,
      fields: [
        { name: 'curso', label: 'Curso', type: 'select', options: ['Preparatório para Engenharia', 'Preparatório para Saúde', 'Preparatório para Direito', 'Preparatório para Economia'], icon: <FiBook />, required: true },
        { name: 'turno', label: 'Turno', type: 'select', options: ['Manhã', 'Tarde', 'Noite'], icon: <FiCalendar />, required: true }
      ]
    },
    {
      title: "Informações de Contato",
      icon: <FiMail />,
      fields: [
        { name: 'email', label: 'Email', type: 'email', icon: <FiMail />, required: true },
        { name: 'telefone', label: 'Telefone', type: 'tel', icon: <FiPhone />, required: true }
      ]
    }
  ];

  return (
    <FormContainer>
      <Header>
        <img src={LogoTipo} alt="Instituto Atlântida" style={{height: '50px',marginBottom: '1rem'}} />
        <HeaderTitle>Pré-Inscrição Acadêmica</HeaderTitle>
        <HeaderSubtitle>Preencha o formulário para iniciar sua jornada conosco</HeaderSubtitle>
      </Header>

      <ProgressBar>
        {steps.map((step, index) => (
          <ProgressStep key={index} active={currentStep > index} current={currentStep === index + 1}>
            <ProgressIcon>{step.icon}</ProgressIcon>
            <ProgressText>{step.title}</ProgressText>
          </ProgressStep>
        ))}
      </ProgressBar>

      <Form onSubmit={handleSubmit}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: currentStep > steps.length ? 0 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: currentStep > steps.length ? 0 : -50 }}
            transition={{ duration: 0.3 }}
          >
            <StepTitle>
              <StepIcon>{steps[currentStep - 1].icon}</StepIcon>
              {steps[currentStep - 1].title}
            </StepTitle>

            <FieldsGrid>
              {steps[currentStep - 1].fields.map((field, index) => (
                <FormGroup key={index}>
                  <Label>
                    <FieldIcon>{field.icon}</FieldIcon>
                    {field.label}
                    {field.required && <Required>*</Required>}
                  </Label>
                  {field.type === 'select' ? (
                    <Select 
                      name={field.name} 
                      value={formData[field.name]} 
                      onChange={handleChange}
                      required={field.required}
                    >
                      <option value="">Selecione...</option>
                      {field.options.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </Select>
                  ) : (
                    <Input 
                      type={field.type} 
                      name={field.name} 
                      value={formData[field.name]} 
                      onChange={handleChange} 
                      placeholder={field.label}
                      required={field.required}
                    />
                  )}
                </FormGroup>
              ))}
            </FieldsGrid>
          </motion.div>
        </AnimatePresence>

        <NavigationButtons>
          {currentStep > 1 && (
            <PrevButton type="button" onClick={prevStep}>
              Voltar
            </PrevButton>
          )}
          {currentStep < steps.length ? (
            <NextButton type="button" onClick={nextStep}>
              Próximo <FiArrowRight />
            </NextButton>
          ) : (
            <SubmitButton type="submit">
              Enviar Inscrição
            </SubmitButton>
          )}
        </NavigationButtons>
      </Form>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        style={customModalStyles}
        contentLabel="Inscrição Realizada"
      >
        <ModalContent>
          <SuccessIcon />
          <ModalTitle>Inscrição Realizada com Sucesso!</ModalTitle>
          <ModalText>
            Obrigado por se inscrever no Instituto Superior Politécnico Atlântida. 
            Seus dados foram recebidos e em breve entraremos em contato.
          </ModalText>
          <ModalButton onClick={() => setModalIsOpen(false)}>Fechar</ModalButton>
        </ModalContent>
      </Modal>
    </FormContainer>
  );
};

// Estilos
const FormContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #00761F 0%, #004614 100%);
  color: white;
  padding: 2rem;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const HeaderSubtitle = styled.p`
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const ProgressBar = styled.div`
  display: flex;
  padding: 1.5rem 2rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const ProgressStep = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  &:not(:last-child):after {
    content: '';
    position: absolute;
    top: 16px;
    left: 50%;
    right: -50%;
    height: 2px;
    background: ${props => props.active ? '#00761F' : '#e9ecef'};
    z-index: 1;
  }
`;

const ProgressIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.current ? '#00761F' : props.active ? '#a5d6a7' : '#e9ecef'};
  color: ${props => props.current ? 'white' : props.active ? '#00761F' : '#adb5bd'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  z-index: 2;
`;

const ProgressText = styled.span`
  font-size: 0.8rem;
  font-weight: ${props => props.current ? '600' : 'normal'};
  color: ${props => props.current ? '#00761F' : props.active ? '#495057' : '#adb5bd'};
  text-align: center;
`;

const Form = styled.form`
  padding: 2rem;
`;

const StepTitle = styled.h2`
  display: flex;
  align-items: center;
  color: #00761F;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const StepIcon = styled.div`
  margin-right: 0.8rem;
  background: #e8f5e9;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00761F;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #495057;
  font-weight: 500;
  font-size: 0.95rem;
`;

const FieldIcon = styled.span`
  margin-right: 0.5rem;
  color: #00761F;
`;

const Required = styled.span`
  color: #e53935;
  margin-left: 0.2rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s;

  &:focus {
    border-color: #00761F;
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 118, 31, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.7rem center;
  background-size: 1rem;
  transition: all 0.3s;

  &:focus {
    border-color: #00761F;
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 118, 31, 0.2);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const BaseButton = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const PrevButton = styled(BaseButton)`
  background: white;
  color: #00761F;
  border: 1px solid #00761F;

  &:hover {
    background: #f1f8e9;
  }
`;

const NextButton = styled(BaseButton)`
  background: #00761F;
  color: white;
  border: none;
  margin-left: auto;

  &:hover {
    background: #00611a;
    transform: translateX(5px);
  }
`;

const SubmitButton = styled(BaseButton)`
  background: #00761F;
  color: white;
  border: none;
  margin-left: auto;
  padding: 0.8rem 2rem;

  &:hover {
    background: #00611a;
    box-shadow: 0 4px 12px rgba(0, 118, 31, 0.3);
  }
`;

const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    borderRadius: '12px',
    padding: '2.5rem',
    border: 'none',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  }
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const SuccessIcon = styled(FiCheckCircle)`
  color: #00761F;
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: #00761F;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ModalText = styled.p`
  color: #495057;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ModalButton = styled.button`
  padding: 0.8rem 2rem;
  background: #00761F;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #00611a;
    transform: translateY(-2px);
  }
`;

export default PublicApplicationForm;